import { ref, computed, watch, onMounted, onBeforeUnmount, resolveComponent, withDirectives, openBlock, createElementBlock, normalizeClass, createBlock, createCommentVNode, createVNode, withCtx, createElementVNode, renderSlot, vShow } from 'vue';
import { createComponent } from './utils/create-component';
import propTypes from './utils/prop-types';
import { INPUT_EVENT, MASK_CLICK_EVENT, BEFORE_HIDE_EVENT, BEFORE_SHOW_EVENT, HIDE_EVENT, SHOW_EVENT } from './utils/constants';
import { useLockScroll } from './utils/use-lock-scroll';
import Mask from './mask';
import Transition from './transition';
import Icon from './icon';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}
function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}
function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var props = {
  /**
   * 弹出层是否可见
   */
  modelValue: propTypes["boolean"]().def(false),
  /**
   *  是否展示蒙层
   */
  showMask: propTypes["boolean"]().def(true),
  /**
   * 点击蒙层是否关闭弹出层
   */
  maskCloseable: propTypes["boolean"]().def(true),
  /**
   * 自定义蒙层类名
   */
  maskClass: propTypes.string().def(''),
  /**
   * 自定义蒙层样式
   */
  maskStyle: propTypes.object(),
  /**
   * 弹出层位置
   */
  position: propTypes.string().def('center'),
  /**
   * 弹出层过渡效果
   */
  transition: propTypes.string().def(''),
  /**
   * 是否禁止滚动穿透
   */
  disableScroll: propTypes["boolean"]().def(true),
  /**
   * 是否将绝对定位的弹出层移到body节点下
   */
  transferDom: propTypes["boolean"]().def(true),
  /**
   * 移到body节点下的弹出层的class
   */
  popupClass: propTypes.string(),
  /**
   * 底部弹出容器添加留白和圆角
   */
  addMargin: propTypes["boolean"]().def(false),
  /**
   * 是否需要遮罩
   */
  needMask: propTypes["boolean"]().def(true),
  /**
   * 底部或顶部弹出时增加安全距离(底部与 add-margin 共同使用时生效)
   */
  needSafeArea: propTypes["boolean"]().def(true),
  /**
   * 底部弹出时显示关闭图标
   */
  closeable: propTypes["boolean"]().def(false)
};
var emits = [INPUT_EVENT, MASK_CLICK_EVENT, BEFORE_HIDE_EVENT, BEFORE_SHOW_EVENT, HIDE_EVENT, SHOW_EVENT];

var _components;
var _createComponent = createComponent('popup'),
  create = _createComponent.create,
  className = _createComponent.className;
var script = create({
  components: (_components = {}, _defineProperty(_components, Mask.name, Mask), _defineProperty(_components, Transition.name, Transition), _defineProperty(_components, Icon.name, Icon), _components),
  props: props,
  emits: emits,
  setup: function setup(props, _ref) {
    var emit = _ref.emit;
    var popup = ref();
    var isPopupBoxShow = ref(false);
    var isPopupShow = ref(false);
    // popup节点是否被移动到了body下
    var domTransferred = false;
    // 背景锁定/不锁定
    var _useLockScroll = useLockScroll(function () {
        return props.disableScroll;
      }),
      _useLockScroll2 = _slicedToArray(_useLockScroll, 2),
      lockScroll = _useLockScroll2[0],
      unlockScroll = _useLockScroll2[1];
    var popupClasses = computed(function () {
      var position = props.position;
      return ["".concat(className), "".concat(className, "--").concat(position)];
    });
    var popupBoxClasses = computed(function () {
      var classNameArr = ["".concat(className, "__box"), props.transition];
      if (props.position === 'bottom' && props.addMargin || props.position === 'top') {
        classNameArr.push("".concat(className, "__box-margin"));
        if (props.needSafeArea) {
          classNameArr.push("".concat(className, "__box-safe"));
        }
      }
      return classNameArr;
    });
    var transitionName = computed(function () {
      if (props.transition) return props.transition;
      switch (props.position) {
        case 'top':
          return 'slide-down';
        case 'right':
          return 'slide-left';
        case 'bottom':
          return 'slide-up';
        case 'left':
          return 'slide-right';
        default:
          return 'fade-grow';
      }
    });
    function showPopupBox() {
      lockScroll();
      if (props.transferDom && !domTransferred) {
        var popupEle = popup.value;
        if (popupEle) {
          domTransferred = true;
          var popupClass = props.popupClass;
          if (popupClass && popup) {
            popupEle.classList && popupEle.classList.add(popupClass);
          }
          document.body.appendChild(popupEle);
        }
      }
      isPopupShow.value = true;
      isPopupBoxShow.value = true;
    }
    function hidePopupBox() {
      isPopupBoxShow.value = false;
      unlockScroll();
    }
    function handlePopupTransitionStart() {
      if (!isPopupBoxShow.value) {
        emit(BEFORE_HIDE_EVENT);
      } else {
        emit(BEFORE_SHOW_EVENT);
      }
    }
    function handlePopupTransitionEnd() {
      if (!isPopupBoxShow.value) {
        isPopupShow.value = false;
        emit(HIDE_EVENT);
      } else {
        emit(SHOW_EVENT);
      }
    }
    function handlePopupMaskClick() {
      if (props.maskCloseable) {
        hidePopupBox();
        emit(MASK_CLICK_EVENT);
        onClose();
      }
    }
    function onClose() {
      emit(INPUT_EVENT, false);
    }
    watch(function () {
      return props.modelValue;
    }, function (val) {
      if (val) {
        showPopupBox();
      } else {
        hidePopupBox();
      }
    });
    onMounted(function () {
      props.modelValue && showPopupBox();
    });
    onBeforeUnmount(function () {
      // 若弹出层未关闭，则移除body上的背景锁定class
      props.modelValue && unlockScroll();
      var ele = popup.value;
      ele && ele.parentNode && ele.parentNode.removeChild(ele);
    });
    return {
      popup: popup,
      className: className,
      isPopupBoxShow: isPopupBoxShow,
      isPopupShow: isPopupShow,
      popupClasses: popupClasses,
      popupBoxClasses: popupBoxClasses,
      transitionName: transitionName,
      handlePopupMaskClick: handlePopupMaskClick,
      handlePopupTransitionStart: handlePopupTransitionStart,
      handlePopupTransitionEnd: handlePopupTransitionEnd,
      onClose: onClose
    };
  }
});

function render(_ctx, _cache, $props, $setup, $data, $options) {
  var _component_sp_mask = resolveComponent("sp-mask");
  var _component_sp_icon = resolveComponent("sp-icon");
  var _component_sp_transition = resolveComponent("sp-transition");
  return withDirectives((openBlock(), createElementBlock("div", {
    "class": normalizeClass(_ctx.popupClasses),
    ref: "popup"
  }, [_ctx.needMask ? (openBlock(), createBlock(_component_sp_mask, {
    key: 0,
    modelValue: _ctx.showMask && _ctx.isPopupBoxShow,
    maskClass: "".concat(_ctx.className, "__mask ").concat(_ctx.maskClass),
    maskStyle: _ctx.maskStyle,
    transition: "popup-mask-transition",
    onClick: _ctx.handlePopupMaskClick
  }, null, 8 /* PROPS */, ["modelValue", "maskClass", "maskStyle", "onClick"])) : createCommentVNode("v-if", true), createVNode(_component_sp_transition, {
    name: _ctx.transitionName,
    onBeforeEnter: _ctx.handlePopupTransitionStart,
    onBeforeLeave: _ctx.handlePopupTransitionStart,
    onAfterEnter: _ctx.handlePopupTransitionEnd,
    onAfterLeave: _ctx.handlePopupTransitionEnd
  }, {
    "default": withCtx(function () {
      return [withDirectives(createElementVNode("div", {
        "class": normalizeClass(_ctx.popupBoxClasses)
      }, [_ctx.closeable ? (openBlock(), createBlock(_component_sp_icon, {
        key: 0,
        name: "dialog_colse",
        size: "24",
        "class": normalizeClass("".concat(_ctx.className, "__box__close")),
        onClick: _ctx.onClose
      }, null, 8 /* PROPS */, ["class", "onClick"])) : createCommentVNode("v-if", true), renderSlot(_ctx.$slots, "default")], 2 /* CLASS */), [[vShow, _ctx.isPopupBoxShow]])];
    }),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["name", "onBeforeEnter", "onBeforeLeave", "onAfterEnter", "onAfterLeave"])], 2 /* CLASS */)), [[vShow, _ctx.isPopupShow]]);
}

script.render = render;
script.__file = "components/src/component/popup/popup.vue";

/*
 * @Description: Popup 弹出层组件导出文件
 * @Author: duqiao@kuaishou.com
 * @Date: 2022-02-28 16:47:23
 * @LastEditors: zhoushichao@kuaishou.com
 * @LastEditTime: 2023-02-02 11:11:40
 */
var _Popup = script;

export { _Popup as default };
